import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Contact, NewContact } from "@/api/queries";
import { Button, Input, Select } from "@/components/ui";

/**
 * The phone and email lists of *Agregar contacto* (design screen 07).
 *
 * As many of each as the person has, edited in place: a row per value, *Quitar*
 * on each, *+ Agregar* under the list. A blank row is a row not saved — which is
 * what lets the modal open with one ready to type into, instead of a button to
 * find first.
 *
 * The whole list goes up on save and the server makes the rows match it
 * (`crm.services.channels`), so a removed row here is a deleted number there.
 */

type PhoneKind = NonNullable<NonNullable<NewContact["phones"]>[number]["kind"]>;

/** `ContactPhone.Kind`: móvil, fijo, WhatsApp. */
const PHONE_KINDS: PhoneKind[] = [1, 2, 3];

export type PhoneRow = {
  key: number;
  id?: number;
  phone: string;
  kind: PhoneKind;
  extension: string;
};

export type EmailRow = { key: number; id?: number; email: string };

let lastKey = 0;
/** A key that survives removing the row above it, which an index does not. */
function nextKey(): number {
  lastKey += 1;
  return lastKey;
}

function blankPhone(): PhoneRow {
  return { key: nextKey(), phone: "", kind: 1, extension: "" };
}

function blankEmail(): EmailRow {
  return { key: nextKey(), email: "" };
}

/** The contact's numbers as rows, or one blank row to type into. */
export function phoneRows(contact?: Contact): PhoneRow[] {
  const rows = (contact?.phones ?? []).map((phone) => ({
    key: nextKey(),
    id: phone.id,
    phone: phone.phone,
    // Both have model defaults, so the schema calls them optional.
    kind: phone.kind ?? 1,
    extension: phone.extension ?? "",
  }));
  return rows.length > 0 ? rows : [blankPhone()];
}

export function emailRows(contact?: Contact): EmailRow[] {
  const rows = (contact?.emails ?? []).map((email) => ({
    key: nextKey(),
    id: email.id,
    email: email.email,
  }));
  return rows.length > 0 ? rows : [blankEmail()];
}

/**
 * What goes up: blank rows dropped, ids kept. The id is what tells the server
 * "this number, changed" apart from "a new number" — without it, editing a
 * digit would delete the row and its call history's link with it.
 */
export function phonesBody(rows: PhoneRow[]) {
  return rows
    .filter((row) => row.phone.trim())
    .map(({ id, phone, kind, extension }) => ({
      ...(id === undefined ? {} : { id }),
      phone: phone.trim(),
      kind,
      extension: extension.trim(),
    }));
}

export function emailsBody(rows: EmailRow[]) {
  return rows
    .filter((row) => row.email.trim())
    .map(({ id, email }) => ({
      ...(id === undefined ? {} : { id }),
      email: email.trim(),
    }));
}

/**
 * Add, change and remove, for either list. The row just added takes the focus,
 * so *+ Agregar teléfono* and then typing is one motion.
 */
function useRows<Row extends { key: number }>(
  rows: Row[],
  onChange: (rows: Row[]) => void,
  blank: () => Row,
) {
  const [focusKey, setFocusKey] = useState<number | null>(null);
  return {
    focusKey,
    add() {
      const row = blank();
      setFocusKey(row.key);
      onChange([...rows, row]);
    },
    update(key: number, patch: Partial<Row>) {
      onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    },
    remove(key: number) {
      onChange(rows.filter((row) => row.key !== key));
    },
  };
}

export function PhoneList({
  rows,
  onChange,
}: {
  rows: PhoneRow[];
  onChange: (rows: PhoneRow[]) => void;
}) {
  const { t } = useTranslation();
  const list = useRows(rows, onChange, blankPhone);

  return (
    <fieldset className="flex flex-col gap-2.5">
      <legend className="kicker mb-2 text-mute-600">{t("newContact.phones")}</legend>
      {rows.map((row, index) => {
        const n = index + 1;
        return (
          <div key={row.key} className="flex flex-wrap items-center gap-2">
            <Input
              type="tel"
              inputMode="tel"
              autoComplete="off"
              maxLength={32}
              className="min-w-0 flex-[1_1_11rem]"
              aria-label={t("newContact.phoneN", { n })}
              placeholder={t("newContact.phonePlaceholder")}
              value={row.phone}
              autoFocus={row.key === list.focusKey}
              onChange={(event) => list.update(row.key, { phone: event.target.value })}
            />
            <Select
              className="w-auto"
              aria-label={t("newContact.phoneKindN", { n })}
              value={row.kind}
              onChange={(event) =>
                list.update(row.key, { kind: Number(event.target.value) as PhoneKind })
              }
            >
              {PHONE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`phoneKind.${kind}`)}
                </option>
              ))}
            </Select>
            <Input
              className="w-20"
              maxLength={8}
              aria-label={t("newContact.extensionN", { n })}
              placeholder={t("newContact.extensionPlaceholder")}
              value={row.extension}
              onChange={(event) =>
                list.update(row.key, { extension: event.target.value })
              }
            />
            <Button
              variant="ghost"
              aria-label={t("newContact.removePhone", { n })}
              onClick={() => list.remove(row.key)}
            >
              {t("newContact.remove")}
            </Button>
          </div>
        );
      })}
      <div>
        <Button variant="ghost" onClick={list.add}>
          {t("newContact.addPhone")}
        </Button>
      </div>
    </fieldset>
  );
}

export function EmailList({
  rows,
  onChange,
}: {
  rows: EmailRow[];
  onChange: (rows: EmailRow[]) => void;
}) {
  const { t } = useTranslation();
  const list = useRows(rows, onChange, blankEmail);

  return (
    <fieldset className="flex flex-col gap-2.5">
      <legend className="kicker mb-2 text-mute-600">{t("newContact.emails")}</legend>
      {rows.map((row, index) => {
        const n = index + 1;
        return (
          <div key={row.key} className="flex flex-wrap items-center gap-2">
            <Input
              type="email"
              autoComplete="off"
              maxLength={200}
              className="min-w-0 flex-[1_1_14rem]"
              aria-label={t("newContact.emailN", { n })}
              placeholder={t("newContact.emailPlaceholder")}
              value={row.email}
              autoFocus={row.key === list.focusKey}
              onChange={(event) => list.update(row.key, { email: event.target.value })}
            />
            <Button
              variant="ghost"
              aria-label={t("newContact.removeEmail", { n })}
              onClick={() => list.remove(row.key)}
            >
              {t("newContact.remove")}
            </Button>
          </div>
        );
      })}
      <div>
        <Button variant="ghost" onClick={list.add}>
          {t("newContact.addEmail")}
        </Button>
      </div>
    </fieldset>
  );
}
